import { useState, useCallback, useEffect } from 'react';
import { Plus, Trash2, Loader2, Link2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { api, ContactRelationship, ContactDetail } from '@/lib/api';

const RELATIONSHIP_TYPES = [
  'Colleague',
  'Manager',
  'Report',
  'Spouse',
  'Partner',
  'Friend',
  'Sibling',
  'Parent',
  'Child',
  'Referral',
  'Client',
  'Vendor',
  'Mentor',
  'Other',
];

export function RelationshipsSection({ contactId }: { contactId: string }) {
  const [relationships, setRelationships] = useState<ContactRelationship[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Search for contacts to link
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ContactDetail[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ContactDetail | null>(null);
  const [relationshipType, setRelationshipType] = useState('Colleague');

  const fetchRelationships = useCallback(async () => {
    try {
      const result = await api.listRelationships(contactId);
      setRelationships(result.relationships);
    } catch {
      // Non-critical
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    fetchRelationships();
  }, [fetchRelationships]);

  // Debounced contact search
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const result = await api.listContacts({ search: searchQuery, limit: 5 });
        // Filter out the current contact
        setSearchResults(result.contacts.filter((c) => c.id !== contactId));
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, contactId]);

  const handleCreate = useCallback(async () => {
    if (!selectedContact) return;
    setCreating(true);
    try {
      const rel = await api.createRelationship(contactId, {
        relatedContactId: selectedContact.id,
        relationshipType,
      });
      setRelationships((prev) => [rel, ...prev]);
      setShowAddForm(false);
      setSelectedContact(null);
      setSearchQuery('');
      setSearchResults([]);
    } catch (err) {
      toast({
        title: 'Failed to add relationship',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  }, [contactId, selectedContact, relationshipType]);

  const handleDelete = useCallback(async (id: string) => {
    setDeletingId(id);
    try {
      await api.deleteRelationship(id);
      setRelationships((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      toast({
        title: 'Failed to remove relationship',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setDeletingId(null);
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium flex items-center gap-1.5">
          <Link2 className="h-3.5 w-3.5" />
          Relationships
        </Label>
        {!showAddForm && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => setShowAddForm(true)}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add
          </Button>
        )}
      </div>

      {showAddForm && (
        <div className="rounded-md border p-3 space-y-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSelectedContact(null);
              }}
              placeholder="Search contacts..."
              className="pl-8 h-8 text-sm"
            />
          </div>

          {searching && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground py-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Searching...
            </div>
          )}

          {searchResults.length > 0 && !selectedContact && (
            <div className="rounded border max-h-32 overflow-y-auto">
              {searchResults.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-accent flex items-center gap-2"
                  onClick={() => {
                    setSelectedContact(c);
                    setSearchQuery(`${c.firstName} ${c.lastName}`.trim());
                    setSearchResults([]);
                  }}
                >
                  <span className="font-medium">
                    {c.firstName} {c.lastName}
                  </span>
                  {c.company && (
                    <span className="text-xs text-muted-foreground">at {c.company}</span>
                  )}
                </button>
              ))}
            </div>
          )}

          {selectedContact && (
            <div className="flex items-center gap-2 text-sm">
              <Badge variant="secondary">
                {selectedContact.firstName} {selectedContact.lastName}
              </Badge>
              <select
                value={relationshipType}
                onChange={(e) => setRelationshipType(e.target.value)}
                className="text-sm border rounded px-2 py-1 bg-background h-7"
              >
                {RELATIONSHIP_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setShowAddForm(false);
                setSelectedContact(null);
                setSearchQuery('');
                setSearchResults([]);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={creating || !selectedContact}
              onClick={handleCreate}
            >
              {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Link'}
            </Button>
          </div>
        </div>
      )}

      {relationships.length === 0 && !showAddForm ? (
        <p className="text-xs text-muted-foreground">No relationships linked.</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {relationships.map((rel) => (
            <Badge key={rel.id} variant="outline" className="text-xs group/rel pr-1">
              <span className="mr-1 text-muted-foreground">{rel.relationshipType}:</span>
              {rel.relatedContactName || 'Unknown'}
              <button
                type="button"
                className="ml-1 opacity-0 group-hover/rel:opacity-100 transition-opacity hover:text-destructive"
                onClick={() => handleDelete(rel.id)}
                disabled={deletingId === rel.id}
              >
                {deletingId === rel.id ? (
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                ) : (
                  <Trash2 className="h-2.5 w-2.5" />
                )}
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
